/**
 * Etiquetas en español para los enums de la API, en un solo lugar.
 *
 * Antes estaban dispersas y duplicadas: `getStateString` existía en cinco
 * vistas con tres implementaciones distintas, `getTypeString` en dos, y
 * `getRoleString` estaba exportada desde dentro de un SFC —algo que
 * `<script setup>` no permite— e importada por otras tres vistas.
 *
 * Los tipos se declaran acá como uniones de literales que espejan los enums de
 * Java (cl.agile.banco.enums). Se reapuntarán a los de bancojs v2 en cuanto el
 * SDK exponga los siete roles y Mode2FA; hoy `UserRole` de bancojs 1.4.5 omite
 * ADMIN_SYSTEM y ADMIN_CERTIFICATES_MAPPERS.
 *
 * El patrón `Record<Union, string>` fuerza exhaustividad: agregar un valor al
 * enum sin agregar su etiqueta es un error de compilación, no un "Admin"
 * silencioso como ocurría antes.
 */

export type UserRole =
  | "ADMIN"
  | "ADMIN_SYSTEM"
  | "ADMIN_USERS"
  | "ADMIN_CLIENTS"
  | "ADMIN_KEYS"
  | "ADMIN_CERTIFICATES"
  | "ADMIN_CERTIFICATES_MAPPERS";

export type State = "ACTIVE" | "INACTIVE" | "DELETED";
export type CsrState = "PENDING" | "COMPLETED";
export type KeyType = "PERSISTENT" | "WRAPPED" | "WRAPPING";
export type SystemKeyType = "TRANSPORT" | "WRAPPING";
export type CertificatePermission = "ATTENDED_SIGN" | "UNATTENDED_SIGN";
export type Mode2FA = "NONE" | "RSAOTP" | "SMS" | "EMAIL" | "TOTP";
export type UserType = "PERSON" | "CLIENT";

/**
 * La implementación anterior caía en `default: return "Admin"`, así que
 * ADMIN_SYSTEM y ADMIN_CERTIFICATES_MAPPERS se mostraban como "Admin",
 * indistinguibles de un ADMIN total — justamente los dos roles de las
 * secciones nuevas.
 */
const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: "Admin",
  ADMIN_SYSTEM: "Admin Sistema",
  ADMIN_USERS: "Admin Usuarios",
  ADMIN_CLIENTS: "Admin Clientes",
  ADMIN_KEYS: "Admin Llaves",
  ADMIN_CERTIFICATES: "Admin Certificados",
  ADMIN_CERTIFICATES_MAPPERS: "Admin Mapeadores",
};

const STATE_LABELS: Record<State, string> = {
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
  DELETED: "Eliminado",
};

const CSR_STATE_LABELS: Record<CsrState, string> = {
  PENDING: "Pendiente",
  COMPLETED: "Completado",
};

const KEY_TYPE_LABELS: Record<KeyType, string> = {
  PERSISTENT: "PERSISTENTE",
  WRAPPED: "ENCAPSULADA",
  WRAPPING: "ENCAPSULAMIENTO",
};

const SYSTEM_KEY_TYPE_LABELS: Record<SystemKeyType, string> = {
  TRANSPORT: "TRANSPORTE",
  WRAPPING: "ENCAPSULAMIENTO",
};

/**
 * Antes: ATTENDED_SIGN -> "Firmar" y cualquier otra cosa -> "--", con lo que
 * UNATTENDED_SIGN aparecía como "--" pese a ser un permiso real.
 */
const PERMISSION_LABELS: Record<CertificatePermission, string> = {
  ATTENDED_SIGN: "Firma atendida",
  UNATTENDED_SIGN: "Firma desatendida",
};

/**
 * `CLIENT` es una aplicación tercera, pero en toda la app —menú, rutas, títulos—
 * se le llama "cliente". Mostrar "aplicación" en un solo lugar contradecía al
 * resto de la interfaz.
 */
const USER_TYPE_LABELS: Record<UserType, string> = {
  PERSON: "Persona",
  CLIENT: "Cliente",
};

const MODE_2FA_LABELS: Record<Mode2FA, string> = {
  NONE: "Sin 2FA",
  RSAOTP: "RSA OTP",
  SMS: "SMS",
  EMAIL: "Email",
  TOTP: "TOTP",
};

/** Devuelve la etiqueta, o el valor crudo si la API manda algo desconocido. */
function lookup<T extends string>(map: Record<T, string>, val: T | null | undefined): string {
  if (val == null) return "";
  return map[val] ?? String(val);
}

export const roleLabel = (v: UserRole | null | undefined) => lookup(ROLE_LABELS, v);
export const stateLabel = (v: State | null | undefined) => lookup(STATE_LABELS, v);
export const csrStateLabel = (v: CsrState | null | undefined) => lookup(CSR_STATE_LABELS, v);
export const keyTypeLabel = (v: KeyType | null | undefined) => lookup(KEY_TYPE_LABELS, v);
export const systemKeyTypeLabel = (v: SystemKeyType | null | undefined) =>
  lookup(SYSTEM_KEY_TYPE_LABELS, v);
export const permissionLabel = (v: CertificatePermission | null | undefined) =>
  lookup(PERMISSION_LABELS, v);
export const mode2FALabel = (v: Mode2FA | null | undefined) => lookup(MODE_2FA_LABELS, v);
export const userTypeLabel = (v: UserType | null | undefined) => lookup(USER_TYPE_LABELS, v);

/** Todos los roles asignables, para poblar los `v-select` de roles. */
export const ALL_ROLES: readonly UserRole[] = Object.keys(ROLE_LABELS) as UserRole[];

/** Opciones `{ title, value }` listas para un `v-select` de Vuetify 3+. */
export const roleSelectItems = () =>
  ALL_ROLES.map((role) => ({ title: ROLE_LABELS[role], value: role }));
